import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  Fab,
  Dialog,
  DialogContent,
  DialogTitle,
  Breadcrumbs,
  Typography,
  Button,
} from '@material-ui/core';
import TextField from '@components/TextFieldCaseSetting';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import AddIcon from '@material-ui/icons/Add';
import AgGrid from '@components/AgGrid/AgGrid';

import { IconCellRenderer, confirm } from '@trueprodigy/components';
import { RIGHTS } from '@constants';

import { linkedEventsSelector } from '@modules/events/selectors/event';
import * as eventActions from '@modules/events/actions/event';
import { userDataSelector, userStatusSelector } from '@selectors/global';
import { hasRight } from '@utils';

const ImageAttachmentDetail = ({
  eventList,
  eventID,
  selectedAttachment,
  onBack,
}) => {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [eventIDToAdd, setEventIDToAdd] = useState('');

  const dispatch = useDispatch();
  const linkedEvents = useSelector(linkedEventsSelector);
  const userData = useSelector(userDataSelector);
  const userStatus = useSelector(userStatusSelector);
  const isReadOnly = userStatus.get('readOnly');

  useEffect(() => {
    dispatch(
      eventActions.fetchLinkedEvents({
        fileAttachmentID: selectedAttachment.fileAttachmentID,
      }),
    );
  }, []);

  const hasEventRight = () =>
    hasRight(userData.get('userrights').toJS(), RIGHTS.EVENTS.EVENT);

  const onAttach = async () => {
    await dispatch(
      eventActions.attachEventsImages({
        fileAttachmentIDs: [selectedAttachment.fileAttachmentID],
        eventIDs: [Number(eventIDToAdd)],
      }),
    );
    dispatch(
      eventActions.fetchLinkedEvents({
        fileAttachmentID: selectedAttachment.fileAttachmentID,
      }),
    );
    setAddDialogOpen(false);
  };

  const buildColumnDefs = () => [
    { headerName: 'Event ID', field: 'eventID' },
    { headerName: 'Event Type', field: 'eventType' },
    { headerName: 'Event Description', field: 'eventDescription' },
    { headerName: 'Object ID', field: 'objectID' },
    {
      headerName: 'Actions',
      field: 'fileAttachmentActions',
      width: 120,
      maxWidth: 120,
      minWidth: 120,
      cellRenderer: IconCellRenderer,
      cellRendererParams: {
        icons: [
          {
            icon: !isReadOnly && hasEventRight() && 'fa-trash',
            onClick: (props, record) => {
              confirm('link').then(() => {
                dispatch(
                  eventActions.deleteEventLinkedImageForImage({
                    fileAttachmentEventID: record.fileAttachmentEventID,
                  }),
                );
              });
            },
          },
        ],
      },
    },
  ];

  return (
    <div>
      <BreadCrumbWrapper>
        {eventList.length > 0 ? (
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            <Link to="/events/search">Event Search</Link>
            <Link onClick={() => onBack()}>{eventID}</Link>
            <Typography color="textPrimary">Linked Image Attachment</Typography>
            <Typography color="textPrimary">
              {selectedAttachment.fileAttachmentID}
            </Typography>
          </Breadcrumbs>
        ) : (
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            <Link onClick={() => onBack()}>Event Detail</Link>
            <Typography color="textPrimary">Linked Image Attachment</Typography>
            <Typography color="textPrimary">
              {selectedAttachment.fileAttachmentID}
            </Typography>
          </Breadcrumbs>
        )}
      </BreadCrumbWrapper>
      <Wrapper>
        <DescriptionWrapper>
          <DescriptionTitle>Event Linked Image Attachment</DescriptionTitle>
          <DescriptionContentWrapper>
            <div>
              <p>
                Description:{' '}
                <span>{selectedAttachment.attachmentDescription}</span>
              </p>
              <p>
                File: <span>{selectedAttachment.s3ID}</span>
              </p>
            </div>
            <div>
              <p>
                Created By: <span>{selectedAttachment.createdBy}</span>
              </p>
              <p>
                Created Date: <span>{selectedAttachment.createDt}</span>
              </p>
            </div>
          </DescriptionContentWrapper>
        </DescriptionWrapper>
        <Splitter></Splitter>
        <div>
          <TableTitleWrapper>
            <h2>LINKED EVENTS</h2>
            <Fab
              aria-label="Add"
              color="primary"
              disabled={isReadOnly || !hasEventRight()}
              onClick={() => {
                setAddDialogOpen(true);
              }}
            >
              <AddIcon />
            </Fab>
          </TableTitleWrapper>
          <div className="ag-theme-material">
            <AgGrid
              autoResizeToFit
              columnDefs={buildColumnDefs()}
              disablePreferenceSave
              domLayout="autoHeight"
              name="imageAttachmentsGrid"
              rowData={linkedEvents}
            />
          </div>
        </div>
      </Wrapper>
      <Dialog
        open={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle>Input Event ID to link</DialogTitle>
        <DialogContent style={{ padding: '0 20px 20px 20px' }}>
          <div>
            <span style={{ marginRight: 20, lineHeight: '31px' }}>
              Event ID
            </span>
            <TextField
              type="text"
              value={eventIDToAdd}
              onChange={e => setEventIDToAdd(e.target.value)}
            />
          </div>
          <div
            style={{
              marginTop: 20,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button color="primary" disabled={!eventIDToAdd} onClick={onAttach}>
              Link
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

ImageAttachmentDetail.propTypes = {
  eventList: PropTypes.array,
  eventID: PropTypes.any,
  selectedAttachment: PropTypes.any,
  onBack: PropTypes.func.isRequired,
};

const Wrapper = styled.div`
  padding: 20px;
`;

const DescriptionWrapper = styled.div`
  position: relative;
  border: 1px solid #e6e6e6;
  padding: 10px;

  p {
    padding: 5px;
    margin: 0;
  }
`;

const DescriptionTitle = styled.span`
  font-size: 18px;
  font-weight: bold;
  position: absolute;
  top: -14px;
  left: 4px;
  background-color: white;
  padding: 0 5px;
`;

const DescriptionContentWrapper = styled.div`
  display: flex;

  > div {
    margin-right: 20px;
  }

  p > span {
    color: #4856aa;
  }
`;

const Splitter = styled.div`
  border-top: 4px solid #acb8cc;
  margin: 20px 0;
`;

const BreadCrumbWrapper = styled.div`
  padding: 6px 10px;
  background-color: rgb(237, 237, 237);
`;

const TableTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  > h2 {
    margin: 17px 0;
    font-size: 25px;
  }
`;

export default withRouter(ImageAttachmentDetail);
